import { FC, ReactNode } from 'react';
import { resolveTimestamp } from '../../../../helper';
import { IConferenceInformation, IMessageReferenceMeeting, IMessageReferences } from '../../../../model/communication/Message';
import { Link, Typo, Btn, TimeAva, Bx } from '@curry-group/mui-curcuma';
import makeStyles from '@material-ui/core/styles/makeStyles';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { BaseListItem } from '../../../list-items/default';
import { faArrowToRight, faExternalLink } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export interface IMessageContentProps {
  isDeleted?: boolean;
  isOwnMessage?: boolean;
  /**
   * Render the content as markdown. The bot answers with headings, bold text and
   * inline links; the default path escapes HTML and only linkifies bare URLs, so
   * that markup would show up verbatim.
   */
  markdown?: boolean;
  content: string;
  conferenceInfo?: IConferenceInformation;
  onJoinConference?: () => void;
  color?: string;
  references?: IMessageReferences;
  onReferenceSelected?: (type: string, data: any) => void;
}

export const MessageContent: FC<IMessageContentProps> = ({ content, markdown, isDeleted, isOwnMessage, conferenceInfo, onJoinConference, color, references, onReferenceSelected }) => {
  let Content: ReactNode = undefined;
  if (isDeleted) {
    Content = <Typo fontWeight="lighter">Diese Nachricht wurde gelöscht</Typo>;
  } else {
    if (references && references.meeting) {
      Content = <MessageMeetingContent color={color} meeting={references.meeting} onMeetingClicked={() => onReferenceSelected?.('meeting', references.meeting)} />;
    } else if (conferenceInfo && onJoinConference) {
      Content = <MessageConferenceContent conferenceInfo={conferenceInfo} onJoinConference={onJoinConference} />;
    } else if (markdown) {
      Content = <MessageMarkdownContent content={content} onLinkClick={link => onReferenceSelected?.('link', link)} />;
    } else {
      Content = <MessageTextContent
        data={resolveMessageContentLinks(content)}
        onLinkClick={link => onReferenceSelected?.('link', link)}
        isOwnMessage={isOwnMessage}
      />;
    }
  }
  return <Bx fontStyle={isDeleted ? 'italic' : 'normal'}>{Content}</Bx>;
};

export interface IResolvedMessageContent {
  content: string[];
  links: string[];
}

export const linkRegExp = /((http|https):\/\/)(\S+)([a-z]{2,}?)(.*?)( |, |$|\. )/gim;
export function resolveMessageContentLinks(content: string): IResolvedMessageContent {
  const eliminatedHtml = content
    .replace(/&/g, '&amp')
    .replace(/</g, '&lt')
    .replace(/>/g, '&gt')
    .replace(/(\r\n|\n|\r)/gm, ' <br/>');

  return {
    content: eliminatedHtml.replace(linkRegExp, '<<>>|||<<>>').split('<<>>|||<<>>'),
    links: content.match(linkRegExp) ?? []
  };
}

export interface IMessageTextContentProps {
  data: IResolvedMessageContent;
  onLinkClick: (link: string) => void;
  isOwnMessage?: boolean;
}
export interface IMessageTextContentStyleProps {
  isOwnMessage?: boolean;
}

const useStyles = makeStyles(theme => ({
  externalLink: {
    background: 'none',
    cursor: 'pointer',
    display: 'inline',
    fontFamily: theme.typography.fontFamily,
    fontSize: 'inherit',
    lineBreak: 'anywhere',
    padding: 0,
    justifyContent: 'left',
    border: 'none !important',
    borderRadius: 0,
    fontWeight: 400,
    textAlign: 'initial',
    textDecoration: 'underline',
    verticalAlign: 'baseline',
    color: 'inherit',
    '&:hover': {
      textDecoration: 'none',
      background: 'none'
    }
  },
  markdown: {
    // Keep bot markdown inside the bubble: no runaway margins, wrapped code and
    // links, lists indented rather than hanging outside.
    '& p': { margin: 0 },
    '& p + p': { marginTop: theme.spacing(1) },
    '& ul, & ol': { margin: `${theme.spacing(0.5)}px 0`, paddingLeft: theme.spacing(3) },
    '& li + li': { marginTop: theme.spacing(0.5) },
    '& a': { lineBreak: 'anywhere' },
    '& code': { wordBreak: 'break-word' },
    '& pre': { whiteSpace: 'pre-wrap', margin: `${theme.spacing(1)}px 0` },
    '& table': { borderCollapse: 'collapse' },
    '& th, & td': { border: '1px solid rgba(0,0,0,.15)', padding: '2px 6px' }
  }
}));

export const MessageTextContent: React.FC<IMessageTextContentProps> = ({ data, onLinkClick, isOwnMessage }) => {
  const classes = useStyles({ isOwnMessage });
  const location = window.location.origin;
  const externalLinksAllowed = useSelector(state => state.foundation.profile?.externalLinksAllowed);

  const resultArray = Array<React.ReactFragment>();
  for (let i = 0; i < data.content.length; i++) {
    const linkEndRegExp = /( |,|, |\.|\. )$/gim;
    resultArray.push(<Typo key={i} dangerouslySetInnerHTML={{ __html: data.content[i] }} fontWeight="normal" />);
    let punctuationAfterLink = '';
    let linkReplace = '';
    if (data.links.length > i) {
      linkReplace = data.links[i];
      const match = linkEndRegExp.exec(linkReplace);
      if (match && match[0] && match[0] !== ' ') {
        punctuationAfterLink = match[0];
        linkReplace = linkReplace.replace(linkEndRegExp, '');
      }
      const isExternalLink = !linkReplace.startsWith(location);
      
      resultArray.push(
        <Typo key={i + '-link'} fontWeight="normal">
          {isExternalLink && (
            <button
              className={clsx(classes.externalLink)}
              onClick={externalLinksAllowed ? () => window.open(linkReplace, '_blank'): () => onLinkClick(linkReplace)}
            >
              <FontAwesomeIcon icon={faExternalLink} />
              &nbsp;
              {linkReplace}
            </button>
          )}
          {!isExternalLink && (
            <Link to={'//' + linkReplace.replace(/(http|https):\/\//, '')} target="_blank">
              {linkReplace}
            </Link>
          )}
          {!!punctuationAfterLink && punctuationAfterLink}
        </Typo>
      );
    }
  }

  return <>{resultArray}</>;
};

export interface IMessageMarkdownContentProps {
  content: string;
  onLinkClick: (link: string) => void;
}

/**
 * Renders a bot answer as markdown. Headings are toned down to inline-sized text
 * so a "#" heading cannot blow up the chat bubble, and links keep the same
 * external-link gate as plain messages: when the user has not allowed external
 * links, clicking one opens the confirmation instead of the site.
 */
export const MessageMarkdownContent: FC<IMessageMarkdownContentProps> = ({ content, onLinkClick }) => {
  const classes = useStyles({});
  const origin = window.location.origin;
  const externalLinksAllowed = useSelector(state => state.foundation.profile?.externalLinksAllowed);

  const renderLink = ({ href, children }: { href?: string; children?: ReactNode }) => {
    const target = href || '';
    const isExternalLink = !!target && !target.startsWith(origin) && !target.startsWith('/');
    if (!isExternalLink) {
      return <Link to={target} target="_blank">{children}</Link>;
    }
    return (
      <button
        className={clsx(classes.externalLink)}
        onClick={externalLinksAllowed ? () => window.open(target, '_blank') : () => onLinkClick(target)}
      >
        <FontAwesomeIcon icon={faExternalLink} />
        &nbsp;
        {children}
      </button>
    );
  };

  return (
    <Typo component="div" fontWeight="normal" className={clsx(classes.markdown)}>
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: renderLink as any,
          h1: ({ children }) => <Typo variant="h4" component="p">{children}</Typo>,
          h2: ({ children }) => <Typo variant="h5" component="p">{children}</Typo>,
          h3: ({ children }) => <Typo variant="h5" component="p">{children}</Typo>,
          h4: ({ children }) => <Typo variant="body1" component="p" fontWeight="bold">{children}</Typo>,
          h5: ({ children }) => <Typo variant="body1" component="p" fontWeight="bold">{children}</Typo>,
          h6: ({ children }) => <Typo variant="body1" component="p" fontWeight="bold">{children}</Typo>,
        }}
      >
        {content}
      </Markdown>
    </Typo>
  );
};

export interface IMessageConferenceContentProps {
  conferenceInfo: IConferenceInformation;
  onJoinConference: () => void;
}

export const MessageConferenceContent: FC<IMessageConferenceContentProps> = ({ conferenceInfo, onJoinConference }) => {
  if (conferenceInfo.started && !conferenceInfo.ended) {
    return (
      <Btn variant="contained" color="secondary" onClick={() => onJoinConference?.()}>
        An Videokonferenz teilnehmen
      </Btn>
    );
  }

  if (conferenceInfo.ended) {
    return <Typo fontWeight="normal">Beendet: {resolveTimestamp(conferenceInfo.ended)}</Typo>;
  }

  return <></>;
};

export interface IMessageMeetingContentProps {
  meeting: IMessageReferenceMeeting;
  onMeetingClicked?: () => void;
  color?: string;
}

export const MessageMeetingContent: FC<IMessageMeetingContentProps> = ({ meeting, onMeetingClicked, color }) => {
  const start = meeting.interval?.dateFrom;
  const end = meeting.interval?.dateTo;
  return (
    <Bx>
      <BaseListItem
        type=""
        title={meeting.title}
        subtitle={dayjs.unix(start).format('DD.MM.YYYY')}
        avatar={<TimeAva size="large" color="default" fromTime={dayjs.unix(start).toDate().toString()} toTime={dayjs.unix(end).toDate().toString()} />}
        fontcolor={color}
        noButtons={true}
        itemTemplate={'meeting'}
      />
      <Bx mt={2}>
        <Btn variant="contained" color="secondary" onClick={() => onMeetingClicked?.()} startIcon={<FontAwesomeIcon icon={faArrowToRight} />}>
          Zum Meeting
        </Btn>
      </Bx>
    </Bx>
  );
};
